import { Filter, Model, ObservableList, RelationType as NeosRelationType, ViewModel, ViewModelState } from '@neos/core'
import CustomUIViewDataFilterUI from '@src/views/CustomUIViewDataFilterUI/CustomUIViewDataFilterUI'
import CustomUIViewEntityViewParameterUI from '@src/views/CustomUIViewEntityViewParameterUI/CustomUIViewEntityViewParameterUI'
import CustomUIViewPropertyUI from '@src/views/CustomUIViewPropertyUI/CustomUIViewPropertyUI'
import CustomUIViewStyleRuleUI from '@src/views/CustomUIViewStyleRuleUI/CustomUIViewStyleRuleUI'
import CustomSubUIViewUI from '@src/views/CustomSubUIViewUI/CustomSubUIViewUI'
import { DataType } from '@neos/shared'

/**
 * Represents CustomUIViewCreateUI.
 */
export default class CustomUIViewCreateUI extends Model {
  /**
   * Initializes a new instance of the CustomUIViewCreateUI class.
   * 
   * @param item Item.
   * @param viewModel ViewModel.
   */
  constructor(item?: Record<string, unknown>, viewModel?: ViewModel<CustomUIViewCreateUI>) {
    super({
      $new: !item,
      isDefault: false,
      isFallbackDefaultForAllUsers: false,
      sharedAllUsers: false,
      ...item
    }, viewModel)
  }

  /**
   * Gets or sets Created at.
   */
  createdAt?: Date | null

  /**
   * Gets or sets Created by.
   */
  createdBy?: string | null

  /**
   * Gets or sets Data filter.
   */
  dataFilter?: string | null

  /**
   * Gets or sets DataFilterObject.
   */
  dataFilterObject: Filter

  /**
   * Gets or sets Data grouping.
   */
  dataGroups?: string | null

  /**
   * Gets or sets Data sorts.
   */
  dataSorts?: string | null

  /**
   * Gets or sets Description.
   */
  description?: Record<string,string> | null

  /**
   * Gets editedDataFilterObject.
   * 
   * @returns EditedDataFilterObject.
   */
  get editedDataFilterObject(): Filter {
    return this.$viewModel.getProperty('editedDataFilterObject').getValue(this) as Filter
  }

  /**
   * Sets editedDataFilterObject.
   * 
   * @param value EditedDataFilterObject.
   */
  set editedDataFilterObject(value: Filter) {
    this.$viewModel.getProperty('editedDataFilterObject').setValue(this, value)
  }

  /**
   * Gets or sets Identifier.
   */
  id?: string | null

  /**
   * Gets or sets Default view.
   */
  isDefault: boolean

  /**
   * Gets or sets Default view for users without a default custom view.
   */
  isFallbackDefaultForAllUsers: boolean

  /**
   * Gets or sets Name.
   */
  name?: Record<string,string> | null

  /**
   * Gets or sets OriginalOwnerViewModelState.
   */
  originalOwnerViewModelState: ViewModelState

  /**
   * Gets or sets Shared with all.
   */
  sharedAllUsers: boolean

  /**
   * Gets or sets UI view Name.
   */
  uiViewName?: string | null

  /**
   * Gets or sets Updated at.
   */
  updatedAt?: Date | null

  /**
   * Gets or sets Updated by.
   */
  updatedBy?: string | null

  /**
   * Gets or sets Cluster version.
   */
  clusterVersion?: string | null

  /**
   * Gets or sets Neos version.
   */
  neosVersion?: string | null

  /**
   * Gets or sets User identifier.
   */
  userId?: string

  /**
   * Gets or sets DataFilterList.
   */
  dataFilterList: ObservableList<CustomUIViewDataFilterUI>

  /**
   * Gets or sets EntityViewParameterList.
   */
  entityViewParameterList: ObservableList<CustomUIViewEntityViewParameterUI>

  /**
   * Gets or sets PropertyList.
   */
  propertyList: ObservableList<CustomUIViewPropertyUI>

  /**
   * Gets or sets StyleRuleList.
   */
  styleRuleList: ObservableList<CustomUIViewStyleRuleUI>

  /**
   * Gets or sets SubUIViewList.
   */
  subUIViewList: ObservableList<CustomSubUIViewUI>

  /**
   * Gets or sets $relations.
   */
  static $relations = new Map([['dataFilterList', { creator: CustomUIViewDataFilterUI, type: NeosRelationType.collection, embeddedForReading: true, embeddedForWriting: true, autoLoad: true, associations: ['customUIViewId'], path: 'dataFilterList' }], ['entityViewParameterList', { creator: CustomUIViewEntityViewParameterUI, type: NeosRelationType.collection, embeddedForReading: true, embeddedForWriting: true, autoLoad: true, associations: ['customUIViewId'], path: 'entityViewParameterList' }], ['propertyList', { creator: CustomUIViewPropertyUI, type: NeosRelationType.collection, embeddedForReading: true, embeddedForWriting: true, autoLoad: true, associations: ['customUIViewId'], path: 'propertyList' }], ['styleRuleList', { creator: CustomUIViewStyleRuleUI, type: NeosRelationType.collection, embeddedForReading: true, embeddedForWriting: true, autoLoad: true, associations: ['customUIViewId'], path: 'styleRuleList' }], ['subUIViewList', { creator: CustomSubUIViewUI, type: NeosRelationType.collection, embeddedForReading: true, embeddedForWriting: true, autoLoad: true, associations: ['parentId'], path: 'subUIViewList' }]])

  /**
   * Gets or sets $keyProperties.
   */
  static $keyProperties = new Set(['id'])

  /**
   * Gets or sets $unboundProperties.
   */
  static $unboundProperties = new Set(['dataFilterObject','editedDataFilterObject','originalOwnerViewModelState'])

  /**
   * Gets or sets $untrackedProperties.
   */
  static $untrackedProperties = new Set(['dataFilterObject','editedDataFilterObject','originalOwnerViewModelState'])

  /**
   * Gets or sets $properties.
   */
  static $properties = new Map([['createdAt', { dataType: DataType.dateTime, serverSideReadOnly: true, }], ['createdBy', { dataType: DataType.string, serverSideReadOnly: true, }], ['updatedAt', { dataType: DataType.dateTime, serverSideReadOnly: true, }], ['updatedBy', { dataType: DataType.string, serverSideReadOnly: true, }]])
}